import { Injectable } from '@angular/core';
import { LifePlanService } from '@core/services/life-plan.service';
import { LiteService } from '@core/services/lite.service';
import { ReportsService } from '@core/services/reports.service';
import { UiService } from '@core/services/ui.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class LifePlanEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private reportsService: ReportsService,
    private lifePlanService: LifePlanService,
    private liteService: LiteService,
    private uiService: UiService
  ) {}

  /**
   * Ensure life plan insurance targets are generated once any of this actions
   * is performed.
   */
  generateInsurances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.completedLifePlanPersonalDataEdit,
        actions.completedLifePlanProfessionalDataEdit,
        actions.completedLifePlanFinancialDataEdit,
        actions.completedLifePlanInsurancesDataEdit,
        actions.completedFuturePlansEdit,
        actions.completedCompleteFormEdit
      ),
      withLatestFrom(this.store),
      map(([params, appState]) => actions.generateLifePlanInsuranceTargets({ config: appState.config }))
    )
  );

  updateReportsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValues,
        actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits,
        actions.updateLifePlanCurrentProductOnCart,
        actions.updateLifePlanSeveralProductsOnCart,
        actions.updateLifePlanSavingInsuranceTargetTotalAmount,
        actions.updateLifePlanSavingInsuranceTargetSavings,
        actions.updateDefaultLifePlanCurrentProductOnCart
      ),
      withLatestFrom(this.store),
      switchMap(([params, appState]) => this.reportsService.getAndUpdateReportsData(appState.lifePlan.currentLifePlan)),
      map(() => actions.reportsDataUpdateSuccess()),
      catchError(() => of(actions.reportsDataUpdateError()))
    )
  );

  updateLifePlanInsuranceTargetsCurrentProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateAllLifePlanInsuranceTargetsCurrentProducts),
      withLatestFrom(this.store),
      switchMap(([params, appState]) =>
        this.liteService.calculateAllTargetsWithLimitsFromLifePlan(appState.lifePlan.currentLifePlan)
      ),
      map(target => {
        return actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits({
          insuranceTargetId: target.insuranceTarget.id,
          productId: target.insuranceTarget.currentProduct?.id || '',
          liteResponses: target.liteResponses
        });
      }),
      catchError(err => {
        this.uiService.openErrorModal();
        console.error('Error while calculating all life plan insurance targets products.', err);
        return of(actions.updatedInBackgroundLifePlanError());
      })
    )
  );

  triggerUpdateLifePlanInBackground$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateLifePlanInsuranceTargetCurrentProduct),
      map(() => actions.updateInBackgroundLifePlan())
    )
  );

  updateLifePlanInBackground$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateInBackgroundLifePlan),
      withLatestFrom(this.store),
      switchMap(([params, appState]) => {
        // In this case we use the LifePlanService instead of the LifePlanManagerService
        // since we don't want a new FullUpdateLifePlan action to be called, we simply
        // want to update the lifePlan on backend, assuming no changes will be returned
        return this.lifePlanService.updateLifePlan(appState.lifePlan.currentLifePlan);
      }),
      map(() => {
        return actions.updatedInBackgroundLifePlanSuccess();
      }),
      catchError(() => {
        return of(actions.updatedInBackgroundLifePlanError());
      })
    )
  );

  updateProductCurrencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValues,
        actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits
      ),
      withLatestFrom(this.store),
      map(([params, appState]) => {
        return actions.updateLifePlanInsuranceTargetCurrentProductCurrencies({
          insuranceTargetId: params.insuranceTargetId,
          productId: params.productId,
          currencyExchange: appState.config.currencyExchange
        });
      })
    )
  );
}
